import { Box, Chip, Icon, SxProps } from "@outschool/backpack";
import {
  ChildInput,
  EnrollmentWarnings_ActivityFragment,
  EnrollmentWarnings_EnrollmentFragment,
  EnrollmentWarnings_LearnerFragment
} from "@outschool/gql-frontend-generated";
import { fasExclamationTriangle } from "@outschool/icons";
import { Trans, useTranslation } from "@outschool/localization";
import { gql } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import React from "react";

import * as Activity from "../../../../../shared/Activity";
import useSectionString from "../../../../../shared/hooks/useSectionString";
import * as Routes from "../../../../../shared/Routes";
import Link from "../../../Link";
import { getAgeGapFromRange } from "./utils";

type EnrollmentWarningsProps = {
  activity: EnrollmentWarnings_ActivityFragment;
  learner: Omit<EnrollmentWarnings_LearnerFragment, "__typename"> | ChildInput;
  conflictingEnrollment?: EnrollmentWarnings_EnrollmentFragment;
  otherSectionEnrollment?: EnrollmentWarnings_EnrollmentFragment;
  ignoreStrictAgeRange: boolean;
};

function EnrollmentWarning({
  children
}: {
  children: React.ReactChild | React.ReactChildren;
}) {
  return (
    <Box
      sx={{
        marginBottom: "0.25em",
        marginTop: "0.25em",
        display: "flex"
      }}
    >
      <Icon
        icon={fasExclamationTriangle}
        sx={{
          marginTop: "0.25em",
          verticalAlign: "baseline",
          color: "error.500",
          marginRight: "0.5em"
        }}
        fixedWidth
      />
      {children}
    </Box>
  );
}

function AgeWarning({
  activity,
  ignoreStrictAgeRange,
  learner
}: Pick<
  EnrollmentWarningsProps,
  "activity" | "learner" | "ignoreStrictAgeRange"
>) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\learnerItems\\EnrollmentWarnings"
  );
  if (
    !learner.age ||
    (!activity.age_min && !activity.age_max) ||
    // @ts-ignore TS(2531): Object is possibly 'null'.
    (learner.age >= activity.age_min && learner.age <= activity.age_max)
  ) {
    return null;
  }
  const ageDifference =
    learner.age &&
    // @ts-ignore TS(2322): Type 'number | null' is not assignable to type 'nu... Remove this comment to see the full error message
    getAgeGapFromRange(learner.age, [activity.age_min, activity.age_max]);
  const ageRangeString = Activity.ageRangeString(activity, false);
  const showAgeRangeWarning = !ignoreStrictAgeRange && ageDifference >= 2;
  return (
    <EnrollmentWarning>
      <Box>
        {t("The age range for this class is {{ageRangeString}}.", {
          ageRangeString
        })}{" "}
        {showAgeRangeWarning &&
          t`Some teachers may withdraw your learner. Please message \
them after enrolling to explain why your learner should participate.`}
      </Box>
    </EnrollmentWarning>
  );
}

AgeWarning.fragments = {
  activity: gql`
    fragment AgeWarning_activity on Activity {
      uid
      age_min
      age_max
      strictAgeRange {
        min
        max
      }
    }
  `,
  learner: gql`
    fragment AgeWarning_learner on Learner {
      uid
      age
    }
  `,
  section: gql`
    fragment AgeWarning_section on Section {
      uid
      isPublished
      details {
        autoScheduledDraft
      }
    }
  `
};

export function StrictAgeRangeWarning({
  activity,
  ignoreStrictAgeRange,
  learner,
  requestException
}: Pick<
  EnrollmentWarningsProps,
  "activity" | "learner" | "ignoreStrictAgeRange"
> & {
  requestException: Function;
}) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\learnerItems\\EnrollmentWarnings"
  );
  if (
    !(
      learner.age &&
      Activity.learnerOutsideStrictAgeRange(
        activity,
        ignoreStrictAgeRange,
        learner.age
      )
    )
  ) {
    return null;
  }
  const strictAgeRangeString = Activity.strictAgeRangeString(activity);
  return (
    <EnrollmentWarning>
      <Box>
        <Trans t={t}>
          This class has a strict age range of {{ strictAgeRangeString }} years.
          You may{" "}
          <TrackedButton
            variant="link"
            trackingName="compose_age_range_exception_message"
            trackingUniqueId={activity.uid}
            onClick={() => {
              requestException(learner);
            }}
            sx={{ verticalAlign: "baseline" }}
          >
            message the teacher
          </TrackedButton>{" "}
          with more details below and ask for your learner to participate.
        </Trans>
      </Box>
    </EnrollmentWarning>
  );
}

export function ToddlerNotAllowedWarning({
  activity,
  learner
}: Pick<EnrollmentWarningsProps, "activity" | "learner">) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\learnerItems\\EnrollmentWarnings"
  );
  if (!(learner.age && Activity.toddlerIsNotAllowed(activity, learner.age))) {
    return null;
  }
  return (
    <EnrollmentWarning>
      <Box>
        {t`Learners aged 1-2 can only enroll in Together Time classes designed for their specific age group.`}
      </Box>
    </EnrollmentWarning>
  );
}
function ConflictWarning({
  conflictingEnrollment,
  learner
}: Pick<EnrollmentWarningsProps, "learner" | "conflictingEnrollment">) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\learnerItems\\EnrollmentWarnings"
  );
  if (!conflictingEnrollment) {
    return null;
  }
  const activityName =
    conflictingEnrollment?.section?.activity?.title || "another class";

  return (
    <EnrollmentWarning>
      <Box>
        <Box
          sx={{
            fontWeight: "fontWeightBold"
          }}
        >{t`Possible schedule conflict`}</Box>
        <Box>
          {t(
            "{{learnerName}} is also enrolled in {{activityName}}, which has some overlapping times.",
            { learnerName: learner.name, activityName }
          )}
        </Box>
      </Box>
    </EnrollmentWarning>
  );
}

ConflictWarning.fragments = {
  enrollment: gql`
    fragment ConflictWarning_enrollment on Enrollment {
      uid
      section {
        uid
        activity {
          uid
          title
          titleTranslated
        }
      }
    }
  `,
  learner: gql`
    fragment ConflictWarning_learner on Learner {
      uid
      name
    }
  `
};

function EnrolledInOtherSectionWarning({
  activity,
  learner,
  otherSectionEnrollment
}: Pick<
  EnrollmentWarningsProps,
  "activity" | "learner" | "otherSectionEnrollment"
>) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\learnerItems\\EnrollmentWarnings"
  );
  const { fullDurationStringNotClub } = useSectionString();
  const { userTimeZone } = useSession();
  if (!otherSectionEnrollment) {
    return null;
  }
  return (
    <EnrollmentWarning>
      <Box>
        <Box>
          {t(
            "{{learnerName}} is already enrolled in another section of this class:",
            { learnerName: learner.name }
          )}
        </Box>
        <Link
          to={
            !!otherSectionEnrollment.section
              ? Routes.sectionAdminPath(otherSectionEnrollment.section)
              : ""
          }
        >
          {fullDurationStringNotClub({
            activity,
            // @ts-ignore TS(2322): Type '{ __typename?: "Section" | undefined; uid: s... Remove this comment to see the full error message
            section: otherSectionEnrollment.section,
            nextOngoingMeeting:
              // @ts-ignore TS(2531): Object is possibly 'null'.
              otherSectionEnrollment.section.nextOngoingMeeting,
            // @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
            userTimeZone
          })}
        </Link>
        {
          // eslint-disable-next-line i18next/no-literal-string
          "."
        }
      </Box>
    </EnrollmentWarning>
  );
}

EnrolledInOtherSectionWarning.fragments = {
  activity: gql`
    fragment EnrolledInOtherSectionWarning_activity on Activity {
      uid
      weekly_meetings
      duration_weeks
      is_ongoing_weekly
      size_max
      published_at
      isClub
      hasTeacherSchedule
    }
  `,
  enrollment: gql`
    fragment EnrolledInOtherSectionWarning_enrollment on Enrollment {
      uid
      section {
        uid
        start_time
        end_time
        nextOngoingMeeting {
          uid
          start_time
        }
      }
    }
  `,
  learner: gql`
    fragment EnrolledInOtherSectionWarning_learner on Learner {
      uid
      name
    }
  `
};

export function OneOnOneEnrolledInOtherSectionWarning({
  otherSectionEnrollment,
  sx
}: {
  otherSectionEnrollment: EnrollmentWarningsProps["otherSectionEnrollment"];
  sx?: SxProps;
}) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\learnerItems\\EnrollmentWarnings"
  );

  if (!otherSectionEnrollment) {
    return null;
  }
  return (
    <Chip
      sx={sx}
      size="small"
      label={t("Currently tutoring with {{teacherName}}", {
        teacherName:
          otherSectionEnrollment?.section?.leader?.name ?? "a teacher"
      })}
    />
  );
}

OneOnOneEnrolledInOtherSectionWarning.fragments = {
  enrollment: gql`
    fragment OneOnOneEnrolledInOtherSectionWarning_enrollment on Enrollment {
      uid
      section {
        uid
        leader {
          uid
          name
        }
      }
    }
  `
};

export function EnrollmentWarnings({
  activity,
  ignoreStrictAgeRange,
  learner,
  conflictingEnrollment,
  otherSectionEnrollment
}: EnrollmentWarningsProps) {
  return (
    <Box>
      <AgeWarning
        activity={activity}
        learner={learner}
        ignoreStrictAgeRange={ignoreStrictAgeRange}
      />
      <ConflictWarning
        conflictingEnrollment={conflictingEnrollment}
        learner={learner}
      />
      {!activity.hasTeacherSchedule && (
        <EnrolledInOtherSectionWarning
          activity={activity}
          otherSectionEnrollment={otherSectionEnrollment}
          learner={learner}
        />
      )}
    </Box>
  );
}

EnrollmentWarnings.fragments = {
  activity: gql`
    fragment EnrollmentWarnings_activity on Activity {
      hasTeacherSchedule
      size_max
      size_min
      ...AgeWarning_activity
      ...EnrolledInOtherSectionWarning_activity
    }
    ${AgeWarning.fragments.activity}
    ${EnrolledInOtherSectionWarning.fragments.activity}
  `,
  section: gql`
    fragment EnrollmentWarnings_section on Section {
      ...AgeWarning_section
    }
    ${AgeWarning.fragments.section}
  `,
  enrollment: gql`
    fragment EnrollmentWarnings_enrollment on Enrollment {
      ...ConflictWarning_enrollment
      ...EnrolledInOtherSectionWarning_enrollment
      ...OneOnOneEnrolledInOtherSectionWarning_enrollment
    }
    ${ConflictWarning.fragments.enrollment}
    ${EnrolledInOtherSectionWarning.fragments.enrollment}
    ${OneOnOneEnrolledInOtherSectionWarning.fragments.enrollment}
  `,
  learner: gql`
    fragment EnrollmentWarnings_learner on Learner {
      ...AgeWarning_learner
      ...ConflictWarning_learner
      ...EnrolledInOtherSectionWarning_learner
    }
    ${AgeWarning.fragments.learner}
    ${ConflictWarning.fragments.learner}
    ${EnrolledInOtherSectionWarning.fragments.learner}
  `
};

export default EnrollmentWarnings;
